"use client";

import { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { useSession } from "next-auth/react";

// La fenêtre n'est téléchargée que le jour où il y a un badge à fêter.
const BadgeCelebration = dynamic(() => import("./BadgeCelebration"), { ssr: false });

// Guette le badge du mois que le membre vient de gagner, pour le fêter dans l'onglet qu'il regarde.
//
// Pour ne rien demander inutilement : la session dit déjà s'il y a un badge à fêter
// (badgeToCelebrate, posé par le rafraîchissement habituel). Sans ce drapeau, aucun appel.
// Avec lui, l'onglet attend d'être au premier plan et libre (pas de fenêtre déjà ouverte), puis
// réserve le badge auprès du serveur : celui-ci ne le donne qu'une fois, donc les autres onglets
// ouverts ne montrent rien.
export default function BadgeCelebrationWatcher() {
    const { data: session } = useSession();
    const pending = Boolean(session?.user?.badgeToCelebrate) && !session?.stale && !session?.revoked;
    const [badge, setBadge] = useState(null);
    const asking = useRef(false);
    const done = useRef(false);

    useEffect(() => {
        if (!pending || done.current) return undefined;
        const tryClaim = async () => {
            // Un seul onglet : celui qui est affiché et a la main. Et jamais par-dessus une autre
            // fenêtre (coffre, quiz en plein écran) : celles-ci bloquent le défilement de la page.
            if (asking.current || done.current) return;
            if (document.visibilityState !== "visible" || !document.hasFocus() || document.body.style.overflow === "hidden") return;
            asking.current = true;
            try {
                const response = await fetch("/api/badges/celebration", { method: "POST" });
                const data = response.ok ? await response.json() : null;
                if (data) {
                    // Réponse reçue : gagné ou déjà pris par un autre onglet, on ne redemande plus.
                    done.current = true;
                    if (data.badge) setBadge(data.badge);
                }
            } catch {
                // Réseau coupé : la prochaine tentative réessaiera.
            } finally {
                asking.current = false;
            }
        };
        tryClaim();
        window.addEventListener("focus", tryClaim);
        document.addEventListener("visibilitychange", tryClaim);
        // Tant qu'une autre fenêtre occupe l'écran, on attend qu'elle se ferme.
        const timer = setInterval(tryClaim, 2500);
        return () => {
            window.removeEventListener("focus", tryClaim);
            document.removeEventListener("visibilitychange", tryClaim);
            clearInterval(timer);
        };
    }, [pending]);

    return badge ? <BadgeCelebration badge={badge} onClose={() => setBadge(null)} /> : null;
}
